"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import {
  Key,
  Copy,
  Check,
  Loader2,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
  Clock,
  Shield,
  Code,
  Send,
  Ban,
  Eye,
  EyeOff,
  FileText,
  CheckCircle,
  XCircle,
  HelpCircle,
  User,
  ChevronRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Badge } from "@/app/_module/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/app/_module/components/ui/alert";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/_module/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useBranding } from "@/app/_module/store/contexts/brandingContext";
import {
  agentApiService,
  type AgentApiAccess,
  type AgentApiStats,
  type AgentApiLeadSubmission,
  type RequestApiAccessParams,
} from "@/app/_module/services/agentApiService";
import { isDemoMode } from "@/app/_module/utils/demoMode";

// Format phone number to (xxx) xxx-xxxx
const formatPhoneNumber = (phone: string | undefined): string => {
  if (!phone) return "";
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, "");
  // Handle 10-digit US numbers (with or without leading 1)
  const normalizedDigits = digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
  if (normalizedDigits.length === 10) {
    return `(${normalizedDigits.slice(0, 3)}) ${normalizedDigits.slice(3, 6)}-${normalizedDigits.slice(6)}`;
  }
  // Return original if not a standard format
  return phone;
};

// Status colors and labels
const statusConfig: Record<
  string,
  { color: string; bg: string; label: string; icon: React.ReactNode }
> = {
  pending: {
    color: "text-yellow-700",
    bg: "bg-yellow-100",
    label: "Pending Review",
    icon: <Clock className="h-4 w-4" />,
  },
  approved: {
    color: "text-blue-700",
    bg: "bg-blue-100",
    label: "Approved",
    icon: <CheckCircle className="h-4 w-4" />,
  },
  active: {
    color: "text-green-700",
    bg: "bg-green-100",
    label: "Active",
    icon: <CheckCircle className="h-4 w-4" />,
  },
  denied: {
    color: "text-red-700",
    bg: "bg-red-100",
    label: "Denied",
    icon: <XCircle className="h-4 w-4" />,
  },
  suspended: {
    color: "text-orange-700",
    bg: "bg-orange-100",
    label: "Suspended",
    icon: <Ban className="h-4 w-4" />,
  },
  revoked: {
    color: "text-gray-700",
    bg: "bg-gray-100",
    label: "Revoked",
    icon: <XCircle className="h-4 w-4" />,
  },
};

export function ApiAccessSettings() {
  const { branding } = useBranding();
  const { toast } = useToast();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [access, setAccess] = useState<AgentApiAccess | null>(null);
  const [stats, setStats] = useState<AgentApiStats | null>(null);
  const [submissions, setSubmissions] = useState<AgentApiLeadSubmission[]>([]);
  const [submissionsTotal, setSubmissionsTotal] = useState(0);
  const [submissionSearch, setSubmissionSearch] = useState("");
  const [submissionLimit, setSubmissionLimit] = useState(50);
  const [submissionPage, setSubmissionPage] = useState(1);

  // Request form state
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState<RequestApiAccessParams>({
    businessName: "",
    websiteUrl: "",
    intendedUse: "",
    expectedVolume: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // API Key display state
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState(false);

  // Dialogs
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  // Active tab
  const [activeTab, setActiveTab] = useState("overview");

  // API key setup flow state
  const [needsApiKeySetup, setNeedsApiKeySetup] = useState(false);
  const [apiKeyConfirmed, setApiKeyConfirmed] = useState(false);

  // Check if agent needs to set up their API key (first time after approval)
  useEffect(() => {
    if (access?.status === "active" && !newApiKey) {
      // If they already have an API key (apiKeyPrefix exists), they don't need setup
      if (access.apiKeyPrefix) {
        setNeedsApiKeySetup(false);
        return;
      }
      // Check localStorage to see if they've already confirmed their key
      const confirmedKey = localStorage.getItem(`api_key_confirmed_${access.id}`);
      if (!confirmedKey) {
        setNeedsApiKeySetup(true);
      }
    }
  }, [access, newApiKey]);

  // Handle API key setup confirmation
  const handleConfirmApiKeySaved = () => {
    if (access) {
      localStorage.setItem(`api_key_confirmed_${access.id}`, "true");
    }
    setNeedsApiKeySetup(false);
    setApiKeyConfirmed(true);
    setNewApiKey(null);
    toast({
      title: "Setup Complete",
      description: "You're all set! Your API is ready to use.",
    });
  };

  // Fetch data (silently = don't show loading spinner for auto-refresh)
  const fetchData = useCallback(async (silently = false) => {
    try {
      if (!silently) {
        setIsLoading(true);
      }

      const statusResponse = await agentApiService.getStatus();
      setAccess(statusResponse.access);

      if (statusResponse.access && statusResponse.access.status === "active") {
        // Fetch stats and submissions in parallel
        const [statsResponse, submissionsResponse] = await Promise.all([
          agentApiService.getStats(),
          agentApiService.getSubmissions({ limit: 10 }),
        ]);

        setStats(statsResponse.stats);
        setSubmissions(submissionsResponse.submissions);
        setSubmissionsTotal(submissionsResponse.total);
      }
    } catch (error: unknown) {
      // Only show error toast for non-silent fetches
      if (!silently) {
        console.error("Failed to fetch API access data:", error);
        toast({
          title: "Error",
          description: "Failed to load API access information.",
          variant: "destructive",
        });
      }
    } finally {
      if (!silently) {
        setIsLoading(false);
      }
    }
  }, [toast]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 5 seconds when page is visible and user has active access
  useEffect(() => {
    if (!access || access.status !== "active") return;

    const interval = setInterval(() => {
      // Only refresh if the page is visible
      if (document.visibilityState === "visible") {
        fetchData(true); // Silent refresh
      }
    }, 5000); // 5 seconds

    return () => clearInterval(interval);
  }, [access, fetchData]);

  // Handle request submission
  const handleSubmitRequest = async () => {
    // DEMO SANDBOX: an API-access request is a real request for a real credential.
    //
    // It creates a pending row an admin then has to review and reject, so a sandbox
    // visitor pressing this generates work for a human — and any key eventually issued
    // would outlive the seat. Gated on submit rather than on the button so the visitor
    // still sees the whole form and understands what the feature is.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Requesting API access is disabled in the demo sandbox. Start a free trial to request a real API key.",
        }),
      );
      return;
    }

    if (!requestForm.businessName.trim() || !requestForm.intendedUse.trim()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await agentApiService.requestAccess(requestForm);
      setAccess(response.access);
      setShowRequestForm(false);
      setRequestForm({
        businessName: "",
        websiteUrl: "",
        intendedUse: "",
        expectedVolume: "",
      });

      toast({
        title: "Request Submitted",
        description:
          "Your API access request has been submitted for review. You will be notified once it's approved.",
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to submit request.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle API key regeneration
  const handleRegenerateApiKey = async () => {
    try {
      setIsRegenerating(true);

      const response = await agentApiService.regenerateApiKey();

      // Handle various response structures
      const apiKey = response?.apiKey || (response as any)?.data?.apiKey;
      const accessData = response?.access || (response as any)?.data?.access;

      if (!apiKey) {
        throw new Error("No API key returned from server");
      }

      if (accessData) {
        setAccess((prev) => prev ? { ...prev, ...accessData } : prev);
      }
      setNewApiKey(apiKey);
      setShowApiKey(true);
      setShowRegenerateDialog(false);

      toast({
        title: "API Key Generated",
        description:
          "Your new API key has been generated. Make sure to copy it now - it won't be shown again!",
      });
    } catch (error: unknown) {
      console.error("Regenerate API Key Error:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to regenerate API key.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  // Handle API access revocation
  const handleRevokeAccess = async () => {
    try {
      setIsRevoking(true);

      const response = await agentApiService.revokeAccess();
      setAccess(response.access);
      setShowRevokeDialog(false);
      setNewApiKey(null);

      toast({
        title: "API Access Revoked",
        description: "Your API access has been revoked. You can request new access if needed.",
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to revoke access.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsRevoking(false);
    }
  };

  // Copy API key to clipboard
  const copyApiKey = () => {
    if (newApiKey) {
      navigator.clipboard.writeText(newApiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: "Copied!",
        description: "API key copied to clipboard.",
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-muted-foreground">Loading API access...</p>
        </div>
      </div>
    );
  }

  // No access - show request form or button
  if (!access || access.status === "revoked" || access.status === "denied") {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-600" />
              API Access
            </CardTitle>
            <CardDescription>
              Connect our API and webhook to your website or funnels to automatically send leads to your {branding.brandName} account.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {access?.status === "denied" && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Request Denied</AlertTitle>
                <AlertDescription>
                  Your previous request was denied.
                  {access.denialReason && (
                    <span className="block mt-1">
                      Reason: {access.denialReason}
                    </span>
                  )}
                  <span className="block mt-2">
                    You can submit a new request below.
                  </span>
                </AlertDescription>
              </Alert>
            )}

            {/* Benefits section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <Send className="h-6 w-6 text-blue-600 mb-2" />
                <h3 className="font-medium text-sm">Auto-Import Leads</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Leads from your funnel are automatically added to your account
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <Shield className="h-6 w-6 text-green-600 mb-2" />
                <h3 className="font-medium text-sm">Secure API Key</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Enterprise-grade security with IP whitelisting support
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                <Code className="h-6 w-6 text-purple-600 mb-2" />
                <h3 className="font-medium text-sm">Easy Integration</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Simple REST API with comprehensive documentation
                </p>
              </div>
            </div>

            {!showRequestForm ? (
              <Button
                onClick={() => setShowRequestForm(true)}
                className="w-full bg-primary hover:bg-primary/90"
                size="lg"
              >
                <Key className="h-4 w-4 mr-2" />
                Request API Access
              </Button>
            ) : (
              <div className="space-y-4 border-t pt-6">
                <h3 className="font-medium">Request API Access</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="businessName">
                      Agency Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="businessName"
                      placeholder=""
                      value={requestForm.businessName}
                      onChange={(e) =>
                        setRequestForm((prev) => ({
                          ...prev,
                          businessName: e.target.value,
                        }))
                      }
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="websiteUrl">Website URL</Label>
                    <Input
                      id="websiteUrl"
                      type="url"
                      placeholder="https://insuracentral.com"
                      value={requestForm.websiteUrl}
                      onChange={(e) =>
                        setRequestForm((prev) => ({
                          ...prev,
                          websiteUrl: e.target.value,
                        }))
                      }
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="intendedUse">
                    How will you use the API? <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="intendedUse"
                    placeholder="Describe your use case..."
                    value={requestForm.intendedUse}
                    onChange={(e) =>
                      setRequestForm((prev) => ({
                        ...prev,
                        intendedUse: e.target.value,
                      }))
                    }
                    disabled={isSubmitting}
                    className="min-h-[100px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expectedVolume">Expected Monthly Lead Volume</Label>
                  <Input
                    id="expectedVolume"
                    placeholder="100-500 leads per month"
                    value={requestForm.expectedVolume}
                    onChange={(e) =>
                      setRequestForm((prev) => ({
                        ...prev,
                        expectedVolume: e.target.value,
                      }))
                    }
                    disabled={isSubmitting}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowRequestForm(false)}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitRequest}
                    disabled={isSubmitting}
                    className="bg-primary hover:bg-primary/90"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Request
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pending status
  if (access.status === "pending") {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-600" />
              API Access
            </CardTitle>
            <CardDescription>
              Your API access request status
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Alert className="bg-yellow-50 border-yellow-200">
              <Clock className="h-4 w-4 text-yellow-600" />
              <AlertTitle className="text-yellow-800">Pending Review</AlertTitle>
              <AlertDescription className="text-yellow-700">
                Your API access request is being reviewed by our team. You will
                be notified once it&apos;s approved. This usually takes 1-2 business
                days.
              </AlertDescription>
            </Alert>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg space-y-3">
              <h4 className="font-medium text-sm">Request Details</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Business Name</p>
                  <p className="font-medium">{access.businessName}</p>
                </div>
                {access.websiteUrl && (
                  <div>
                    <p className="text-muted-foreground">Website</p>
                    <p className="font-medium">{access.websiteUrl}</p>
                  </div>
                )}
                <div className="col-span-2">
                  <p className="text-muted-foreground">Intended Use</p>
                  <p className="font-medium">{access.intendedUse}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Submitted</p>
                  <p className="font-medium">
                    {new Date(access.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Suspended status
  if (access.status === "suspended") {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-orange-600" />
              API Access
            </CardTitle>
            <CardDescription>
              Your API access status
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Alert variant="destructive" className="bg-orange-50 border-orange-200">
              <Ban className="h-4 w-4 text-orange-600" />
              <AlertTitle className="text-orange-800">Access Suspended</AlertTitle>
              <AlertDescription className="text-orange-700">
                Your API access has been temporarily suspended. Please contact
                support for more information.
                {access.reviewNotes && (
                  <span className="block mt-1">
                    Note: {access.reviewNotes}
                  </span>
                )}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  // API Key Setup Flow - Show when agent needs to get their API key for the first time
  if (needsApiKeySetup && access.status === "active") {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Your API Access is Approved!</CardTitle>
            <CardDescription className="text-base">
              Welcome to {branding.brandName} API. Let&apos;s get you set up.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {!newApiKey ? (
              <>
                {/* Step 1: Generate API Key */}
                <div className="text-center space-y-4">
                  <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
                    <Key className="h-12 w-12 mx-auto text-blue-600 mb-4" />
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">
                      Generate Your API Key
                    </h3>
                    <p className="text-sm text-blue-700 mb-4">
                      Click the button below to generate your unique API key. You&apos;ll need this to connect your website or funnel to {branding.brandName}.
                    </p>
                    <Button
                      onClick={handleRegenerateApiKey}
                      disabled={isRegenerating}
                      size="lg"
                      className="bg-primary hover:bg-primary/90"
                    >
                      {isRegenerating ? (
                        <>
                          <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Key className="h-5 w-5 mr-2" />
                          Generate My API Key
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Your API key will only be shown once. Make sure to copy and save it securely.
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Step 2: Copy and Confirm API Key */}
                <div className="space-y-4">
                  <Alert className="bg-green-50 border-green-200">
                    <Key className="h-4 w-4 text-green-600" />
                    <AlertTitle className="text-green-800">Your API Key is Ready!</AlertTitle>
                    <AlertDescription className="text-green-700">
                      Copy your API key below and save it somewhere secure. This key will not be shown again.
                    </AlertDescription>
                  </Alert>

                  <div className="p-4 bg-gray-50 rounded-lg border space-y-3">
                    <Label className="text-sm font-medium">Your API Key</Label>
                    <div className="flex items-center gap-2 p-3 bg-white rounded-lg border">
                      <code className="flex-1 font-mono text-sm break-all">
                        {showApiKey ? newApiKey : "••••••••••••••••••••••••••••••••••••••••"}
                      </code>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowApiKey(!showApiKey)}
                            >
                              {showApiKey ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <Eye className="h-4 w-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {showApiKey ? "Hide" : "Show"} API key
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button
                        variant={copied ? "default" : "outline"}
                        size="sm"
                        onClick={copyApiKey}
                        className={copied ? "bg-green-600 hover:bg-green-700" : ""}
                      >
                        {copied ? (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <Alert className="bg-yellow-50 border-yellow-200">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-700 text-sm">
                      <strong>Important:</strong> Store this key securely. You won&apos;t be able to see it again. If you lose it, you&apos;ll need to regenerate a new one.
                    </AlertDescription>
                  </Alert>

                  <div className="pt-4">
                    <Button
                      onClick={handleConfirmApiKeySaved}
                      size="lg"
                      className="w-full bg-green-600 hover:bg-green-700"
                      disabled={!copied}
                    >
                      <Check className="h-5 w-5 mr-2" />
                      I&apos;ve Copied and Saved My API Key - Continue
                    </Button>
                    {!copied && (
                      <p className="text-xs text-center text-muted-foreground mt-2">
                        Please copy your API key first to continue
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active status - show full dashboard
  const statusInfo = statusConfig[access.status] || statusConfig.pending;

  return (
    <div className="space-y-6">
      {/* New API Key Alert */}
      {newApiKey && (
        <Alert className="bg-green-50 border-green-200">
          <Key className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">API Key Generated</AlertTitle>
          <AlertDescription className="text-green-700">
            <p className="mb-3">
              <strong>Important:</strong> Copy your API key now. It will not be
              shown again for security reasons.
            </p>
            <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-green-200">
              <code className="flex-1 font-mono text-sm break-all">
                {showApiKey ? newApiKey : "••••••••••••••••••••••••••••••••"}
              </code>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {showApiKey ? "Hide" : "Show"} API key
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="outline"
                size="sm"
                onClick={copyApiKey}
                className="bg-white"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Main Card with Tabs */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-blue-600" />
                API Access
              </CardTitle>
              <CardDescription>
                Manage your API integration for lead submission
              </CardDescription>
            </div>
            <Badge className={`${statusInfo.bg} ${statusInfo.color} gap-1`}>
              {statusInfo.icon}
              {statusInfo.label}
            </Badge>
          </div>
        </CardHeader>

        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="submissions">
                Submissions
                {submissionsTotal > 0 && (
                  <Badge variant="secondary" className="ml-2 px-1.5 py-0.5 text-xs">
                    {submissionsTotal}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="docs">Documentation</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6 mt-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-600 font-medium">Total Submitted</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {stats?.totalLeadsSubmitted || access.totalLeadsSubmitted || 0}
                  </p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                  <p className="text-xs text-green-600 font-medium">Successful</p>
                  <p className="text-2xl font-bold text-green-900">
                    {stats?.totalLeadsSuccess || access.totalLeadsSuccess || 0}
                  </p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                  <p className="text-xs text-red-600 font-medium">Failed</p>
                  <p className="text-2xl font-bold text-red-900">
                    {stats?.totalLeadsFailed || access.totalLeadsFailed || 0}
                  </p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                  <p className="text-xs text-purple-600 font-medium">Last 30 Days</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {stats?.last30Days?.submitted || 0}
                  </p>
                </div>
              </div>

              {/* API Key Info */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">API Key</h4>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRegenerateDialog(true)}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Regenerate
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowRevokeDialog(true)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Ban className="h-4 w-4 mr-2" />
                      Revoke
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Key Prefix</p>
                    <p className="font-mono font-medium">
                      {access.apiKeyPrefix || "N/A"}...
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p className="font-medium">
                      {access.apiKeyCreatedAt
                        ? new Date(access.apiKeyCreatedAt).toLocaleDateString()
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Rate Limit</p>
                    <p className="font-medium">
                      {access.rateLimitPerMinute} requests/min
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Last Used</p>
                    <p className="font-medium">
                      {access.lastApiCallAt
                        ? new Date(access.lastApiCallAt).toLocaleDateString()
                        : "Never"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Agency Info */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                <h4 className="font-medium">Agency Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Agency Name</p>
                    <p className="font-medium">{access.businessName}</p>
                  </div>
                  {access.websiteUrl && (
                    <div>
                      <p className="text-muted-foreground">Website</p>
                      <a
                        href={access.websiteUrl.startsWith("http") ? access.websiteUrl : `https://${access.websiteUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 hover:underline flex items-center gap-1"
                      >
                        {access.websiteUrl}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Submissions Tab */}
            <TabsContent value="submissions" className="mt-6">
              {/* Header with search and refresh */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
                  <Input
                    placeholder="Search by name, phone, or reference..."
                    value={submissionSearch}
                    onChange={(e) => {
                      setSubmissionSearch(e.target.value);
                      setSubmissionPage(1);
                    }}
                    className="max-w-xs"
                  />
                  <select
                    value={submissionLimit}
                    onChange={(e) => {
                      setSubmissionLimit(Number(e.target.value));
                      setSubmissionPage(1);
                    }}
                    className="px-2 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={500}>500</option>
                  </select>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchData(true)}
                  className="gap-1.5"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Refresh
                </Button>
              </div>

              {(() => {
                // Filter submissions based on search
                const filteredSubmissions = submissions.filter((submission) => {
                  if (!submissionSearch.trim()) return true;
                  const search = submissionSearch.toLowerCase();
                  return (
                    submission.leadName?.toLowerCase().includes(search) ||
                    submission.leadPhone?.toLowerCase().includes(search) ||
                    submission.externalLeadId?.toLowerCase().includes(search) ||
                    submission.id?.toLowerCase().includes(search)
                  );
                });

                // Apply pagination
                const totalFiltered = filteredSubmissions.length;
                const totalPages = Math.ceil(totalFiltered / submissionLimit) || 1;
                const startIndex = (submissionPage - 1) * submissionLimit;
                const endIndex = startIndex + submissionLimit;
                const paginatedSubmissions = filteredSubmissions.slice(startIndex, endIndex);

                if (submissions.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-lg font-medium text-gray-600">
                        No submissions yet
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Leads submitted via your API will appear here
                      </p>
                    </div>
                  );
                }

                if (paginatedSubmissions.length === 0 && submissionSearch) {
                  return (
                    <div className="text-center py-12">
                      <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-lg font-medium text-gray-600">
                        No matching submissions
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Try a different search term
                      </p>
                    </div>
                  );
                }

                return (
                  <>
                    <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                      {paginatedSubmissions.map((submission) => {
                        const leadId = submission.leadId || submission.createdLeadId;
                        const isClickable = leadId && (submission.status === "success" || submission.status === "duplicate");

                        const content = (
                          <div
                            className={`flex items-center justify-between p-3 bg-gray-50 rounded-lg border transition-colors ${
                              isClickable ? "hover:bg-gray-100 hover:border-gray-300 cursor-pointer" : ""
                            }`}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {submission.status === "success" ? (
                                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                              ) : submission.status === "failed" ? (
                                <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                              ) : submission.status === "duplicate" ? (
                                <HelpCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                              ) : (
                                <Clock className="h-5 w-5 text-gray-400 flex-shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {submission.leadName ? (
                                    <p className="font-medium text-sm truncate flex items-center gap-1.5">
                                      <User className="h-3.5 w-3.5 text-gray-400" />
                                      {submission.leadName}
                                    </p>
                                  ) : (
                                    <p className="font-medium text-sm text-gray-500">
                                      {submission.externalLeadId || `ID: ${submission.id.slice(0, 8)}`}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                                  <span>{new Date(submission.createdAt).toLocaleString()}</span>
                                  {submission.leadPhone && (
                                    <>
                                      <span>•</span>
                                      <span>{formatPhoneNumber(submission.leadPhone)}</span>
                                    </>
                                  )}
                                  {submission.externalLeadId && submission.leadName && (
                                    <>
                                      <span>•</span>
                                      <span className="text-gray-400">Ref: {submission.externalLeadId}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={
                                  submission.status === "success"
                                    ? "default"
                                    : submission.status === "failed"
                                    ? "destructive"
                                    : "secondary"
                                }
                                className={submission.status === "duplicate" ? "bg-yellow-100 text-yellow-700" : ""}
                              >
                                {submission.status}
                              </Badge>
                              {isClickable && (
                                <ChevronRight className="h-4 w-4 text-gray-400" />
                              )}
                            </div>
                          </div>
                        );

                        if (isClickable) {
                          return (
                            <Link key={submission.id} href={`/leads/${leadId}`}>
                              {content}
                            </Link>
                          );
                        }

                        return <div key={submission.id}>{content}</div>;
                      })}
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex items-center justify-between pt-4 border-t mt-4">
                      <span className="text-sm text-muted-foreground">
                        {startIndex + 1}-{Math.min(endIndex, totalFiltered)} of {totalFiltered}
                        {submissionSearch && ` (filtered from ${submissions.length})`}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSubmissionPage(p => Math.max(1, p - 1))}
                          disabled={submissionPage === 1}
                        >
                          Previous
                        </Button>
                        <span className="text-sm text-muted-foreground px-2">
                          Page {submissionPage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSubmissionPage(p => Math.min(totalPages, p + 1))}
                          disabled={submissionPage === totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </TabsContent>

            {/* Documentation Tab */}
            <TabsContent value="docs" className="mt-6 space-y-6">
              <Alert className="bg-blue-50 border-blue-200">
                <Code className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-800">How to Connect Your Funnel</AlertTitle>
                <AlertDescription className="text-blue-700">
                  Send leads from your website, landing page, or funnel directly to your {branding.brandName} account.
                </AlertDescription>
              </Alert>

              {/* Quick Start */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                <h4 className="font-medium text-green-800 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Quick Start Guide
                </h4>
                <ol className="text-sm text-green-700 space-y-2 list-decimal list-inside">
                  <li>Copy your API endpoint URL below</li>
                  <li>Add it as a webhook URL in your form builder</li>
                  <li>Add your API key to the request headers</li>
                  <li>Map your form fields to our field names</li>
                  <li>Test by submitting a lead - it will appear in your Submissions tab</li>
                </ol>
              </div>

              {/* Endpoint */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Step 1: API Endpoint URL</Label>
                <p className="text-sm text-muted-foreground">
                  Use this URL as your webhook endpoint. This is where your funnel sends lead data.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 bg-gray-900 text-green-400 rounded-lg text-sm font-mono break-all">
                    {typeof window !== "undefined" ? window.location.origin : "https://app.insuracentral.com"}/api/v1/agent-api/leads
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const url = `${typeof window !== "undefined" ? window.location.origin : "https://app.insuracentral.com"}/api/v1/agent-api/leads`;
                      navigator.clipboard.writeText(url);
                      toast({ title: "Copied!", description: "Endpoint URL copied to clipboard." });
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Authentication */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Step 2: Add Your API Key</Label>
                <p className="text-sm text-muted-foreground">
                  Your funnel must include your API key in every request. Add this header:
                </p>
                <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">Header Name</p>
                      <code className="block p-2 bg-gray-900 text-green-400 rounded text-sm font-mono">
                        X-API-Key
                      </code>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">Header Value</p>
                      <code className="block p-2 bg-gray-900 text-green-400 rounded text-sm font-mono">
                        {access.apiKeyPrefix}... (your full API key)
                      </code>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Most form builders have a &quot;Custom Headers&quot; or &quot;HTTP Headers&quot; section where you can add this.
                  </p>
                </div>
              </div>

              {/* Request Format */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Step 3: Map Your Form Fields</Label>
                <p className="text-sm text-muted-foreground">
                  Your form should send data in JSON format. Here&apos;s an example:
                </p>
                <pre className="p-4 bg-gray-900 text-green-400 rounded-lg text-sm font-mono overflow-x-auto">
{`{
  "leads": [
    {
      "first_name": "John",
      "last_name": "Doe",
      "phone": "555-123-4567",
      "email": "john@example.com",
      "address": "123 Main St",
      "city": "Miami",
      "state": "FL",
      "zip": "33101",
      "dob": "1985-03-15",
      "gender": "male",
      "height": "5'10\\"",
      "weight": "180 lbs",
      "marital_status": "married",
      "employment_status": "employed",
      "notes": "Interested in life insurance",
      "external_id": "your-crm-id-123",
      "custom_fields": {
        "coverage_amount": "$500,000",
        "health_rating": "excellent"
      }
    }
  ]
}`}
                </pre>
              </div>

              {/* Complete Example */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Complete Example (cURL)</Label>
                <p className="text-sm text-muted-foreground">
                  Here&apos;s a complete example you can test in your terminal:
                </p>
                <pre className="p-4 bg-gray-900 text-green-400 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap">
{`curl -X POST "${typeof window !== "undefined" ? window.location.origin : "https://app.insuracentral.com"}/api/v1/agent-api/leads" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${access.apiKeyPrefix}..." \\
  -d '{
    "leads": [{
      "first_name": "John",
      "last_name": "Doe",
      "phone": "555-123-4567",
      "email": "john@example.com",
      "state": "FL"
    }]
  }'`}
                </pre>
                <p className="text-xs text-muted-foreground">
                  Replace <code className="bg-gray-200 px-1 rounded">{access.apiKeyPrefix}...</code> with your full API key.
                </p>
              </div>

              {/* Field Reference */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Field Reference</Label>

                {/* Required */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-600">Required Fields</p>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                      <code className="bg-red-100 px-2 py-0.5 rounded font-mono text-red-800">phone</code>
                      <div>
                        <p className="font-medium">Phone Number</p>
                        <p className="text-muted-foreground text-xs">Any format works: (555) 123-4567, 555-123-4567, 5551234567</p>
                      </div>
                    </div>
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                      <div className="flex flex-wrap gap-1">
                        <code className="bg-red-100 px-2 py-0.5 rounded font-mono text-red-800">name</code>
                        <span className="text-red-600 text-xs">or</span>
                        <code className="bg-red-100 px-2 py-0.5 rounded font-mono text-red-800">first_name</code>
                        <span className="text-red-600 text-xs">/</span>
                        <code className="bg-red-100 px-2 py-0.5 rounded font-mono text-red-800">last_name</code>
                      </div>
                      <div>
                        <p className="font-medium">Lead Name</p>
                        <p className="text-muted-foreground text-xs">Use <code className="bg-red-100 px-1 rounded">name</code> for full name, or <code className="bg-red-100 px-1 rounded">first_name</code> and <code className="bg-red-100 px-1 rounded">last_name</code> separately</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* General Information Fields */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-blue-600">General Information Fields</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">email</code>
                      <span className="ml-2 text-muted-foreground">Email address</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">address</code>
                      <span className="ml-2 text-muted-foreground">Street address</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">city</code>
                      <span className="ml-2 text-muted-foreground">City</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">state</code>
                      <span className="ml-2 text-muted-foreground">State code (e.g., FL, TX)</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">zip</code>
                      <span className="ml-2 text-muted-foreground">ZIP code</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">dob</code>
                      <span className="ml-2 text-muted-foreground">Date of birth (any format - auto-normalized)</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">age</code>
                      <span className="ml-2 text-muted-foreground">Age</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">gender</code>
                      <span className="ml-2 text-muted-foreground">male, female, or other</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">height</code>
                      <span className="ml-2 text-muted-foreground">Height (5&apos;10&quot;)</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">weight</code>
                      <span className="ml-2 text-muted-foreground">Weight (180 lbs)</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">notes</code>
                      <span className="ml-2 text-muted-foreground">Lead notes</span>
                    </div>
                  </div>
                </div>

                {/* Income & Employment Fields */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-purple-600">Income &amp; Employment Fields</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">marital_status</code>
                      <span className="ml-2 text-muted-foreground">single/married/divorced/widowed</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">employment_status</code>
                      <span className="ml-2 text-muted-foreground">employed/self-employed/retired/etc</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">earned</code>
                      <span className="ml-2 text-muted-foreground">Earned income</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">unearned</code>
                      <span className="ml-2 text-muted-foreground">Unearned income</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">worth</code>
                      <span className="ml-2 text-muted-foreground">Net worth</span>
                    </div>
                  </div>
                </div>

                {/* Health & Insurance Fields */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-green-600">Health &amp; Insurance Fields</p>
                  <p className="text-xs text-muted-foreground">These fields are stored in custom_fields and shown in &quot;More Information&quot; section</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">blood_pressure</code>
                      <span className="ml-2 text-muted-foreground">Blood pressure</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">cholesterol</code>
                      <span className="ml-2 text-muted-foreground">Cholesterol level</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">smoking_history</code>
                      <span className="ml-2 text-muted-foreground">Smoking history</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">tobacco_use</code>
                      <span className="ml-2 text-muted-foreground">true/false</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">family_medical_history</code>
                      <span className="ml-2 text-muted-foreground">Family history</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">hobbies</code>
                      <span className="ml-2 text-muted-foreground">Array of hobbies</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">travel_frequency</code>
                      <span className="ml-2 text-muted-foreground">Travel frequency</span>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <code className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-xs">existing_policies</code>
                      <span className="ml-2 text-muted-foreground">Array of policies</span>
                    </div>
                  </div>
                </div>

                {/* Optional/Tracking */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-purple-600">Tracking & Custom Fields</p>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <code className="bg-purple-100 px-2 py-0.5 rounded font-mono text-purple-800">external_id</code>
                        <div>
                          <p className="font-medium">Your Reference ID</p>
                          <p className="text-muted-foreground text-xs">Your own lead ID from your CRM or funnel for easy tracking</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-start gap-3">
                        <code className="bg-purple-100 px-2 py-0.5 rounded font-mono text-purple-800">custom_fields</code>
                        <div>
                          <p className="font-medium">Custom Fields</p>
                          <p className="text-muted-foreground text-xs">
                            Any extra data you collect (coverage amount, smoker status, health info, etc.)
                            will be saved as custom fields on the lead.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Alternative Field Names */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                <h4 className="font-medium text-blue-800">Alternative Field Names</h4>
                <p className="text-sm text-blue-700">
                  We also accept camelCase versions: <code className="bg-blue-100 px-1 rounded">firstName</code>, <code className="bg-blue-100 px-1 rounded">lastName</code>, <code className="bg-blue-100 px-1 rounded">externalId</code>, etc.
                </p>
              </div>

              {/* DOB Format Info */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
                <h4 className="font-medium text-green-800">Date of Birth Formats</h4>
                <p className="text-sm text-green-700">
                  The <code className="bg-green-100 px-1 rounded">dob</code> field accepts multiple formats and is automatically normalized to YYYY-MM-DD:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-green-700">
                  <code className="bg-green-100 px-2 py-1 rounded">1985-03-15</code>
                  <code className="bg-green-100 px-2 py-1 rounded">03/15/1985</code>
                  <code className="bg-green-100 px-2 py-1 rounded">03/15/85</code>
                  <code className="bg-green-100 px-2 py-1 rounded">3/15/85</code>
                  <code className="bg-green-100 px-2 py-1 rounded">15/03/1985</code>
                  <code className="bg-green-100 px-2 py-1 rounded">March 15, 1985</code>
                  <code className="bg-green-100 px-2 py-1 rounded">Mar/15/1985</code>
                  <code className="bg-green-100 px-2 py-1 rounded">3 15 1985</code>
                </div>
                <p className="text-xs text-green-600">
                  European format (DD/MM/YYYY) is also supported.
                </p>
              </div>

              {/* Rate Limits */}
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-medium text-yellow-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Rate Limits
                </h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Maximum {access.rateLimitPerMinute} requests per minute. You can send up to 100 leads per request.
                </p>
              </div>

              {/* Troubleshooting */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Troubleshooting</Label>
                <div className="space-y-2 text-sm">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">Lead not showing up?</p>
                    <p className="text-muted-foreground">Check the Submissions tab above. If the status is &quot;failed&quot;, hover over it to see the error message.</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">Getting &quot;Unauthorized&quot; error?</p>
                    <p className="text-muted-foreground">Make sure your API key is correct and included in the X-API-Key header.</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">Duplicate leads?</p>
                    <p className="text-muted-foreground">We detect duplicates by phone number. If a lead with the same phone exists, we&apos;ll mark it as a duplicate.</p>
                  </div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="font-medium text-blue-800">Need Help?</p>
                    <p className="text-blue-700">
                      Contact our support team at{" "}
                      <a href={`mailto:${branding.supportEmail}`} className="underline font-medium">
                        {branding.supportEmail}
                      </a>
                      {" "}and we&apos;ll help you get set up.
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Regenerate API Key Dialog */}
      <Dialog open={showRegenerateDialog} onOpenChange={setShowRegenerateDialog}>
        <ResponsiveDialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-yellow-600" />
              Regenerate API Key
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to regenerate your API key? Your current key
              will be immediately invalidated and any integrations using it will
              stop working.
            </DialogDescription>
          </DialogHeader>

          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This action cannot be undone. You will need to update your
              integrations with the new API key.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRegenerateDialog(false)}
              disabled={isRegenerating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRegenerateApiKey}
              disabled={isRegenerating}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {isRegenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Regenerating...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate Key
                </>
              )}
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Revoke Access Dialog */}
      <Dialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <ResponsiveDialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="h-5 w-5" />
              Revoke API Access
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke your API access? This will
              permanently disable your API key and stop all lead submissions
              from your funnel.
            </DialogDescription>
          </DialogHeader>

          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This action is permanent. You will need to request new API access
              if you want to use the API again.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRevokeDialog(false)}
              disabled={isRevoking}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevokeAccess}
              disabled={isRevoking}
            >
              {isRevoking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Revoking...
                </>
              ) : (
                <>
                  <Ban className="h-4 w-4 mr-2" />
                  Revoke Access
                </>
              )}
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>
    </div>
  );
}
